import { useEffect, useMemo, useState, useCallback } from 'react';
import { BiToggleLeft } from 'react-icons/bi';
import { HiCurrencyDollar } from "react-icons/hi";
import { useAuth } from '../../../auth/AuthenticationWrapper';
import ClientService from '../../../rest/clientService';
import { useProductData } from '../hooks/useProductData';
import { useProductSelection } from '../hooks/useProductSelection';
import ProductList from './productList';
import useFilters from "../../../components/reusables/filterBar/hooks/useFilters";
import FilterBar from "../../../components/reusables/filterBar/filterBar";
import BulkActionBar, { BulkActionButton } from "../../../components/reusables/bulkActions/bulkActionBar";
import CustomButton from '../../../components/lib/customButton';
import Pagination from '../../../components/lib/pagination';
import ProductRegisterModal from './productRegisterModal';
import CentralModal from '../../../components/lib/centralModal';
import ProductDetailsModalContent from './productDetailsModal';
import BatchProductStatusChangeModal from './bulkActions/batchProductStatusChangeModal';
import StockAdjustmentModal from '../../../components/reusables/stockAdjustmentModal';
import ViewToggle from '../../../components/lib/viewToggle';
import ProductGrid from './productGrid';
import useViewMode from '../../../hooks/useViewMode';
import usePersistedState from '../../../hooks/usePersistedState';

function ProductTable() {
    const [pageSize, setPageSize] = useState(10);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [isStockAdjustmentModalOpen, setIsStockAdjustmentModalOpen] = useState(false);
    const [detailsItem, setDetailsItem] = useState(null);

    // ✅ Structured sort (logic only change)
    const [sort, setSort] = usePersistedState('storage:products:sort', {
        field: null,
        direction: null
    });

    const { hasPermission, user } = useAuth();
    const canCreate = hasPermission("storage", "create");
    // Permissão dedicada ao ajuste manual de estoque, separada de "storage:update" (edição de
    // cadastro de produto). Enforcement real no backend via @PreAuthorize("hasPermission('storage','adjust')")
    // em StorageController.applyManualStockAdjustment — esta checagem de UI é só cosmética/UX.
    const canAdjustStock = hasPermission("storage", "adjust");
    const [viewMode, setViewMode] = useViewMode('foodz_product_view_mode', 'list');

    const {
        search, setSearch,
        filters, setFilters,
        updateFilter, clearFilters,
        page, setPage
    } = useFilters({}, 'storage:products:filters');

    // ✅ Sort toggle (ASC → DESC → NONE)
    const handleSortChange = useCallback((field) => {
        setSort(prev => {
            if (prev.field === field) {
                if (prev.direction === 'asc') return { field, direction: 'desc' };
                if (prev.direction === 'desc') return { field: null, direction: null };
            }
            return { field, direction: 'asc' };
        });
        setPage(0);
    }, [setPage, setSort]);

    useEffect(() => {
        const loadPreferences = async () => {
            try {
                const prefs = await ClientService.getClientAdminPreferences();
                if (prefs?.categories) {
                    setCategoryOptions(
                        prefs.categories
                            .split(";")
                            .map(c => c.trim())
                            .filter(Boolean)
                            .map(c => ({ value: c, label: c }))
                    );
                }
            } catch (error) {
                console.error("Erro prefs:", error);
            }
        };
        loadPreferences();
    }, []);

    const filtersWithSearch = useMemo(
        () => ({ ...filters, search }),
        [filters, search]
    );

    // ✅ Backend-ready sort param — sempre envia ao menos active DESC, name ASC
    const sortParam = useMemo(() => {
        if (!sort.field || !sort.direction) {
            // padrão: ativos primeiro, depois alfabético
            return ['active,desc', 'name,asc'];
        }
        const primary = `${sort.field},${sort.direction}`;
        // evita duplicar quando o próprio campo já é "name"
        return sort.field === 'name' ? [primary] : [primary, 'name,asc'];
    }, [sort]);

    const {
        pagedData,
        totalPages,
        totalElements,
        loading
    } = useProductData(
        filtersWithSearch,
        page,
        pageSize,
        sortParam,
        user
    );

    const selection = useProductSelection(pagedData);

    const handleBatchUpdate = async (isActive) => {
        if (selection.actions?.updateBatchStatus) {
            await selection.actions.updateBatchStatus(isActive);
            setIsStatusModalOpen(false);
        }
    };

    const getSelectedObjects = () => {
        if (!pagedData) return [];
        return pagedData.filter(item =>
            selection.selectedItems.includes(item.product_id)
        );
    };

    const filterConfigs = useMemo(() => [
        ...(categoryOptions.length > 0 ? [{
            key: "categories",
            label: "Categorias",
            options: categoryOptions,
            placeholder: "Categoria",
            selectedValues: filters.categories || [],
            onChange: v => {
                updateFilter("categories", v);
                setPage(0);
            }
        }] : []),
        {
            key: "status",
            label: "Status",
            placeholder: "Status",
            options: [
                { value: true, label: "Ativo" },
                { value: false, label: "Inativo" }
            ],
            selectedValues: filters.status || [],
            onChange: v => {
                updateFilter("status", v);
                setPage(0);
            }
        }
    ], [filters, categoryOptions, updateFilter, setPage]);

    const rangeConfigs = useMemo(() => [{
        key: "price",
        label: "Faixa de Preço",
        min: filters.minPrice,
        max: filters.maxPrice,
        prefix: "R$",
        icon: HiCurrencyDollar,
        onChange: ({ min, max }) => {
            setFilters(prev => ({
                ...prev,
                minPrice: min,
                maxPrice: max
            }));
            setPage(0);
        }
    }], [filters, setFilters, setPage]);

    return (
        <div>
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-6">
                <div className="flex-1 w-full z-10">
                    {selection.selectedItems.length > 0 ? (
                        <BulkActionBar
                            selectedCount={selection.selectedItems.length}
                            onClearSelection={selection.clearSelection}
                        >
                            <BulkActionButton
                                icon={BiToggleLeft}
                                label="Ativar/Desativar"
                                onClick={() => setIsStatusModalOpen(true)}
                                variant="primary"
                            />
                        </BulkActionBar>
                    ) : (
                        <FilterBar
                            searchValue={search}
                            onSearchChange={(val) => {
                                setSearch(val);
                                setPage(0);
                            }}
                            onResetPage={() => setPage(0)}
                            placeholder="Pesquisar produto"
                            filters={filterConfigs}
                            rangeFilters={rangeConfigs}
                            onClearFilters={() => {
                                clearFilters();
                                setPage(0);
                            }}
                            loading={loading}
                        />
                    )}
                </div>

                <ViewToggle view={viewMode} onChange={setViewMode} />

                <div className="flex items-center gap-3 justify-end shrink-0 h-10">
                    <select
                        className='h-full pl-3 pr-8 border border-slate-300 rounded-lg bg-white text-sm outline-none'
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setPage(0);
                        }}
                    >
                        {[10, 20, 30, 50].map(size => (
                            <option key={size} value={size}>
                                {size} itens/pág
                            </option>
                        ))}
                    </select>

                    <div className="h-full">
                        <CustomButton
                            type="button"
                            buttonType="editCredits"
                            showIcon={false}
                            text="Atualizar Estoque"
                            disabled={!canAdjustStock}
                            onClick={() => setIsStockAdjustmentModalOpen(true)}
                            className="h-full flex items-center justify-center whitespace-nowrap"
                        />
                    </div>

                    <div className="h-full">
                        <CustomButton
                            type="button"
                            buttonType="confirm"
                            showIcon={false}
                            text="Adicionar"
                            disabled={!canCreate}
                            onClick={() => setIsProductModalOpen(true)}
                            className="h-full flex items-center justify-center whitespace-nowrap"
                        />
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="w-full py-20 text-center bg-white border-x-2 border-b-2 text-gray-400 animate-pulse">
                    Carregando estoque...
                </div>
            ) : viewMode === 'list' ? (
                <ProductList
                    data={pagedData}
                    selectedItems={selection.selectedItems}
                    onSelectAll={selection.handleSelectAll}
                    onSelectItem={selection.handleSelectItem}
                    onSortChange={handleSortChange}
                    sortField={sort.field}
                    sortDirection={sort.direction}
                    onOpenDetails={setDetailsItem}
                />
            ) : (
                <ProductGrid
                    data={pagedData}
                    onOpenDetails={setDetailsItem}
                />
            )}

            <div className="py-4">
                {totalPages > 1 && (
                    <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        totalItems={totalElements}
                        pageSize={pageSize}
                    />
                )}
            </div>

            <ProductRegisterModal
                isOpen={isProductModalOpen}
                setIsOpen={setIsProductModalOpen}
            />

            <CentralModal
                isOpen={!!detailsItem}
                onClose={() => setDetailsItem(null)}
                title="Detalhes do Produto"
                className="md:max-w-lg lg:max-w-4xl xl:max-w-5xl md:px-4 md:-mr-16"
            >
                {detailsItem && (
                    <ProductDetailsModalContent
                        product={detailsItem}
                        onEdit={() => setDetailsItem(null)}
                        onDelete={() => setDetailsItem(null)}
                    />
                )}
            </CentralModal>

            <BatchProductStatusChangeModal
                isOpen={isStatusModalOpen}
                onClose={() => setIsStatusModalOpen(false)}
                selectedProducts={getSelectedObjects()}
                onConfirm={handleBatchUpdate}
            />

            <StockAdjustmentModal
                isOpen={isStockAdjustmentModalOpen}
                onClose={() => setIsStockAdjustmentModalOpen(false)}
            />
        </div>
    );
}

export default ProductTable;